import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { toastAxiosError } from "components/Utils";
import { downloadAgreementsConcatPDFRep, downloadAgreementsPDFRep, downloadAllFilesRep, DownloadPhotoAlbumRep, downloadSectionsPDFRep, downloadSeparateTemplatesRep, DownloadVirtualFolderRep, DownloadBioInfoRep, DownloadAllRep, exportDataToOMGTRep, generateTemporaryLinkWithAllFilesRep, synchronizeApplicationFormStatusRep, downloadSectionsConcatPDFRep, notifyAppFormUserOfAccountRep } from "features/Repository/StaffDefineRepository";import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { AppThunk } from "../../app/store";
import t from "../../components/I18N/TranslateHelpers";

interface StaffDefineState {
  error: AxiosError | null;
  isSyncing: boolean;
  ongoingActions: string[];
  isRequestingNotify: boolean;
  isSuccessNotify: boolean;
  isFailedNotify: boolean;
}

const initialState: StaffDefineState = {
  error: null,
  isSyncing: false,
  ongoingActions: [],
  isRequestingNotify: false,
  isSuccessNotify: false,
  isFailedNotify: false,
}

function startSyncingRdc(state: StaffDefineState) {
  state.isSyncing = true;
}

function stopSyncingRdc(state: StaffDefineState) {
  state.isSyncing = false;
}

function startRequestRdc(state: StaffDefineState, action: PayloadAction<string>) {
  state.error = null;
  if (!state.ongoingActions.includes(action.payload)) {
    state.ongoingActions.push(action.payload);
  }
}

function requestFailedRdc(state: StaffDefineState, action: PayloadAction<{actionName: string, error: AxiosError}>) {
  state.error = action.payload.error;
  if (state.ongoingActions.includes(action.payload.actionName)) {
    state.ongoingActions = state.ongoingActions.filter(act => act !== action.payload.actionName);
  }
  toast.error(t("error_downloading_file"));
}

function requestSuccessRdc(state: StaffDefineState, action: PayloadAction<string>) {
  state.error = null;
  if (state.ongoingActions.includes(action.payload)) {
    state.ongoingActions = state.ongoingActions.filter(act => act !== action.payload);
  }
}

function startNotifyRdc(state: StaffDefineState) {
  state.isRequestingNotify = true;
  state.isSuccessNotify = false;
  state.isFailedNotify = false;
}

function successNotifyRdc(state: StaffDefineState) {
  state.isRequestingNotify = false;
  state.isSuccessNotify = true;
  state.isFailedNotify = false;
}

function failedNotifyRdc(state: StaffDefineState) {
  state.isRequestingNotify = false;
  state.isSuccessNotify = false;
  state.isFailedNotify = true;
}


const StaffDefineSlice = createSlice(
  {
    name: 'StaffDefine',
    initialState: initialState,
    reducers: {
      startRequestAct: startRequestRdc,
      requestSuccessAct: requestSuccessRdc,
      requestFailedAct: requestFailedRdc,
      startSyncingcAct: startSyncingRdc,
      stopSyncingcAct: stopSyncingRdc,
      startNotifyAct: startNotifyRdc,
      successNotifyAct: successNotifyRdc,
      failedNotifyAct : failedNotifyRdc,
    }
  }
)

export const {
  startRequestAct,
  requestSuccessAct,
  requestFailedAct,
  startSyncingcAct,
  stopSyncingcAct,
  startNotifyAct,
  successNotifyAct,
  failedNotifyAct
} = StaffDefineSlice.actions;

export default StaffDefineSlice.reducer;

export const downloadSectionsPDF = (applicationFormUid: string, actionName: string, isAnonymous: boolean): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await downloadSectionsPDFRep(applicationFormUid, isAnonymous);
    saveAs(apiResponse.blob, apiResponse.filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}

export const downloadAgreementsPDF = (applicationFormUid: string, actionName: string): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await downloadAgreementsPDFRep(applicationFormUid);
    saveAs(apiResponse.blob, apiResponse.filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}

export const downloadAllFiles = (applicationFormUid: string, filename: string, actionName: string): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await downloadAllFilesRep(applicationFormUid);
    saveAs(apiResponse, filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}

export const generateTemporaryLinkWithAllFiles = (applicationFormUid: string, filename: string, actionName: string): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await generateTemporaryLinkWithAllFilesRep(applicationFormUid);
    saveAs(apiResponse, filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}

export const downloadSeparateTemplates = (applicationFormUid: string, filename: string, actionName: string): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await downloadSeparateTemplatesRep(applicationFormUid);
    saveAs(apiResponse, filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}

export const synchronizeApplicationFormStatus = (applicationFormUid: string, applicationFormStatus: number): AppThunk => async dispatch => {
  try {
    dispatch(startSyncingcAct());
    await synchronizeApplicationFormStatusRep({ appFormUid: applicationFormUid, applicationFormStatus: applicationFormStatus });
    dispatch(stopSyncingcAct());
    toast.success(t("sync_omgt_success"));
  } catch (err) {
    dispatch(stopSyncingcAct());
    toastAxiosError(err as AxiosError);
  }
}

export const exportDataToOMGT = (applicationFormUid: string): AppThunk => async dispatch => {
  try {
    dispatch(startSyncingcAct());
    await exportDataToOMGTRep(applicationFormUid);
    dispatch(stopSyncingcAct());
    toast.success(t("export_omgt_success"));
  } catch (err) {
    dispatch(stopSyncingcAct());
    toastAxiosError(err as AxiosError);
  }
}


export const downloadVirtualFolder = (applicationFormUid: string, actionName: string): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await DownloadVirtualFolderRep(applicationFormUid);

    saveAs(apiResponse.blob, apiResponse.filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}

export const downloadBioInfo = (applicationFormUid: string, actionName: string): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await DownloadBioInfoRep(applicationFormUid);

    saveAs(apiResponse.blob, apiResponse.filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}
export const downloadAll = (applicationFormUid: string, actionName: string, isAnonymous : Boolean): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await DownloadAllRep(applicationFormUid, isAnonymous);

    saveAs(apiResponse.blob, apiResponse.filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}

export const downloadPhotoAlbum = (applicationFormUid: string, actionName: string, isAnonymous : Boolean): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await DownloadPhotoAlbumRep(applicationFormUid, isAnonymous);

    saveAs(apiResponse.blob, apiResponse.filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}

export const notifyAppFormUserOfAccount = (applicationFormUid: string, email: string| null = null): AppThunk => async dispatch => {
  try {
    dispatch(startNotifyAct());
    await notifyAppFormUserOfAccountRep(applicationFormUid, email);
    dispatch(successNotifyAct());
    toast.success(t("email_sent"));
  } catch (err) {
    dispatch(failedNotifyAct());
    toastAxiosError(err as AxiosError);
  }
}

export const downloadSectionsConcatPDF = (applicationFormUid: string, actionName: string, isAnonymous: boolean): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await downloadSectionsConcatPDFRep(applicationFormUid, isAnonymous);
    saveAs(apiResponse.blob, apiResponse.filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}

export const downloadAgreementsConcatPDF = (applicationFormUid: string, actionName: string): AppThunk => async dispatch => {
  try {
    dispatch(startRequestAct(actionName));
    const apiResponse = await downloadAgreementsConcatPDFRep(applicationFormUid);
    saveAs(apiResponse.blob, apiResponse.filename);
    dispatch(requestSuccessAct(actionName));
  } catch (err) {
    toastAxiosError(err as AxiosError);
    dispatch(requestFailedAct({actionName, error: err as AxiosError}));
  }
}
